/**
 * T-068: client for GET /admin/report-subscriptions/inventory — the read model
 * behind the Report Subscriptions Inventory page. Mirrors the server DTO
 * (api/services/reportSubscriptionInventory.js). Pause/resume reuses the existing
 * PATCH in reportSubscriptionsApi; this module only reads + exports CSV.
 */
import { adminFetch, getAuthHeaders, API_BASE_URL } from './adminApi';

export type InventoryView = 'subscription' | 'recipient';
export type RecipientStatus = 'active' | 'unsubscribed' | 'suppressed';
export type RecipientReason = 'complaint' | 'bounce' | 'unsubscribed' | 'reports_suppression' | null;

export interface InventoryFilters {
  company_id?: string;
  source?: string;
  cadence?: 'daily' | 'weekly' | 'monthly';
  enabled?: boolean;
  recipient_type?: 'user' | 'external';
  q?: string;
  view?: InventoryView;
  page?: number;
  per_page?: number;
}

export interface ReportsGate {
  allowed: boolean;
  reason: string;
}

export interface InventoryOwner {
  user_id?: string;
  name?: string | null;
  email?: string | null;
  redacted: boolean;
}

export interface InventoryRecipient {
  id: string;
  kind: 'user' | 'external';
  user_id?: string;
  name: string | null;
  email: string | null;
  redacted: boolean;
  key: string;
  status: RecipientStatus;
  primary_reason: RecipientReason;
  reasons: { kind: string; at: string | null }[];
}

export interface InventoryLastAttempt {
  status: 'running' | 'sent' | 'partial' | 'skipped' | 'stale_scope' | 'failed';
  reason: string | null;
  recipients_total: number | null;
  recipients_sent: number | null;
  attempts: number;
  started_at: string;
  finished_at: string | null;
}

export interface InventorySubscriptionRow {
  id: string;
  name: string;
  source: string;
  has_source_ref: boolean;
  company: { id: string; name: string | null };
  scope: { label: string; enforced: boolean };
  cadence: string;
  schedule_label: string;
  timezone: string;
  enabled: boolean;
  next_due_at: string | null;
  last_attempt_at: string | null;
  /** T-212: the latest scheduled send's final status (absent before the attempts table exists). */
  last_attempt?: InventoryLastAttempt | null;
  owner: InventoryOwner;
  recipients: InventoryRecipient[];
  recipient_counts: { users: number; external: number; inactive: number };
}

export interface InventoryRecipientRow {
  key: string;
  email: string | null;
  display_name: string | null;
  redacted: boolean;
  kinds: ('user' | 'external')[];
  user_ids: string[];
  subscription_count: number;
  subscriptions: {
    id: string;
    name: string;
    company: { id: string; name: string | null };
    cadence: string;
    enabled: boolean;
    targets: { kind: 'user' | 'external'; status: RecipientStatus; primary_reason: RecipientReason; reasons: { kind: string; at: string | null }[] }[];
    worst_status: RecipientStatus;
    worst_reason: RecipientReason;
  }[];
  worst_status: RecipientStatus;
  worst_reason: RecipientReason;
}

export interface InventoryResponse<Row = InventorySubscriptionRow | InventoryRecipientRow> {
  view: InventoryView;
  reports_gate: ReportsGate;
  filters: Record<string, string | null>;
  page: number;
  per_page: number;
  total: number;
  items: Row[];
}

function toQuery(filters: InventoryFilters): string {
  const p = new URLSearchParams();
  if (filters.company_id) p.set('company_id', filters.company_id);
  if (filters.source) p.set('source', filters.source);
  if (filters.cadence) p.set('cadence', filters.cadence);
  if (filters.enabled != null) p.set('enabled', String(filters.enabled));
  if (filters.recipient_type) p.set('recipient_type', filters.recipient_type);
  if (filters.q) p.set('q', filters.q);
  if (filters.view) p.set('view', filters.view);
  if (filters.page) p.set('page', String(filters.page));
  if (filters.per_page) p.set('per_page', String(filters.per_page));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** Fetch the inventory (subscription or recipient view). */
export async function getReportInventory(
  filters: InventoryFilters = {},
): Promise<InventoryResponse> {
  return adminFetch<InventoryResponse>(`/report-subscriptions/inventory${toQuery(filters)}`);
}

/**
 * Superadmin-only CSV export. Fetches text/csv with auth (adminFetch JSON-parses,
 * so this uses the raw blob-download pattern) and triggers a browser download.
 */
export async function downloadReportInventoryCsv(filters: InventoryFilters = {}): Promise<void> {
  const query = toQuery({ ...filters, page: undefined, per_page: undefined });
  const url = `${API_BASE_URL}/v1/admin/report-subscriptions/inventory${query}${query ? '&' : '?'}format=csv`;
  const response = await fetch(url, { headers: { ...(await getAuthHeaders() as Record<string, string>) } });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(err.message || `CSV export failed: ${response.status}`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = 'report-subscriptions-inventory.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on a timer — immediate revoke can cancel the download in Safari.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
}
